package com.dotdrives.model

import kotlin.time.Instant
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable data class DonorOrganization(val id: String, val name: String)

@Serializable
data class DonorProfile(
    @SerialName("display_name") val displayName: String,
    val email: String,
    val phone: String? = null,
    val address: String? = null,
)

@Serializable
data class DonorSummary(
    val lifetime: Double,
    @SerialName("year_total") val yearTotal: Double,
    @SerialName("gift_count") val giftCount: Int,
    @SerialName("first_gift_at") val firstGiftAt: Instant? = null,
    @SerialName("cause_count") val causeCount: Int = 0,
)

@Serializable
data class DonorSubscription(
    val id: String,
    val amount: Double,
    val interval: String,
    val status: String,
    @SerialName("next_charge_at") val nextChargeAt: Instant? = null,
    @SerialName("cancel_at_period_end") val cancelAtPeriodEnd: Boolean = false,
    @SerialName("payment_status") val paymentStatus: String? = null,
)

@Serializable
data class DonorSnapshot(
    @SerialName("giver_id") val giverId: String,
    val profile: DonorProfile,
    val summary: DonorSummary,
    val donations: List<GiverGift> = emptyList(),
    val subscriptions: List<DonorSubscription> = emptyList(),
    val statements: List<GiverStatement> = emptyList(),
    @SerialName("next_after") val nextAfter: String? = null,
)

@Serializable data class DonorUpdateBody(val id: String, val body: String)

@Serializable data class DonorAttachment(val id: String, val name: String, val size: Long)

@Serializable
data class DonorUpdate(
    val id: String,
    val title: String,
    @SerialName("shared_at") val sharedAt: Instant? = null,
    @SerialName("opened_at") val openedAt: Instant? = null,
    val updates: List<DonorUpdateBody> = emptyList(),
    val attachments: List<DonorAttachment> = emptyList(),
)

@Serializable
data class DonorRecurringRequest(
    val action: String,
    @SerialName("org_id") val orgId: String,
    @SerialName("subscription_id") val subscriptionId: String,
    @SerialName("operation_id") val operationId: String,
    val amount: String? = null,
    val day: Int? = null,
)

@Serializable
data class DonorRecurringResult(
    val accepted: Boolean = false,
    val status: String? = null,
    val url: String? = null,
    val message: String? = null,
)

interface DonorOperationStore {
    fun read(orgId: String): DonorRecurringRequest?

    fun save(request: DonorRecurringRequest)

    fun clear(orgId: String)
}

class MemoryDonorOperationStore : DonorOperationStore {
    private val values = mutableMapOf<String, DonorRecurringRequest>()

    override fun read(orgId: String) = values[orgId]

    override fun save(request: DonorRecurringRequest) {
        values[request.orgId] = request
    }

    override fun clear(orgId: String) {
        values.remove(orgId)
    }
}

/** Only emitted after the server durably prevents this unsent request from being prepared. */
class RecurringRequestRejected : IllegalStateException(
    "This request was not sent. Reload the schedule before choosing another change.",
)
